/**
 *  TopbarMobileMenu prints the menu content for authenticated user or
 * shows login actions for those who are not authenticated.
 */
import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';

import { ACCOUNT_SETTINGS_PAGES } from '../../../../routing/routeConfiguration';
import { FormattedMessage } from '../../../../util/reactIntl';
import { propTypes } from '../../../../util/types';
import { ensureCurrentUser, isUserAdmin } from '../../../../util/data';
import { useConfiguration } from '../../../../context/configurationContext';

import {
  AvatarLarge,
  InlineTextButton,
  NamedLink,
  NotificationBadge,
} from '../../../../components';

import css from './TopbarMobileMenu.module.css';

const CategoryLinks = props => {
  const { categories } = props;
  return (
    <div className={css.categoryLinksWrapper}>
      {categories.map(category => {
        return (
          <NamedLink
            key={category.id}
            className={css.navigationLink}
            name="SearchPage"
            to={{ search: `?pub_categoryLevel1=${category.id}` }}
          >
            {category.name}
          </NamedLink>
        );
      })}
    </div>
  );
};

const TopbarMobileMenu = props => {
  const {
    isAuthenticated,
    currentPage,
    currentUser,
    notificationCount,
    currentUserCartItemsCount,
    onLogout,
  } = props;

  const config = useConfiguration();
  const { categories: listingCategoriesConfig } = config.categoryConfiguration || {};

  const user = ensureCurrentUser(currentUser);
  const isCurrentUserAdmin = isUserAdmin(user);

  if (!isAuthenticated) {
    const signup = (
      <NamedLink name="SignupPage" className={css.signupLink}>
        <FormattedMessage id="TopbarMobileMenu.signupLink" />
      </NamedLink>
    );

    const login = (
      <NamedLink name="LoginPage" className={css.loginLink}>
        <FormattedMessage id="TopbarMobileMenu.loginLink" />
      </NamedLink>
    );

    const signupOrLogin = (
      <span className={css.authenticationLinks}>
        <FormattedMessage id="TopbarMobileMenu.signupOrLogin" values={{ signup, login }} />
      </span>
    );
    return (
      <div className={css.root}>
        <div className={css.content}>
          <div className={css.authenticationGreeting}>
            <FormattedMessage
              id="TopbarMobileMenu.unauthorizedGreeting"
              values={{ lineBreak: <br />, signupOrLogin }}
            />
          </div>
          <CategoryLinks categories={listingCategoriesConfig} />
          <div className={css.spacer} />
        </div>
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.sellOnMarketplaceLink" />
          </NamedLink>
        </div>
      </div>
    );
  }

  const notificationCountBadge =
    notificationCount.messageNotificationCount > 0 ? (
      <NotificationBadge
        className={css.notificationBadge}
        count={notificationCount.messageNotificationCount}
      />
    ) : null;

  const cartItemsCountBadge =
    currentUserCartItemsCount > 0 ? (
      <NotificationBadge className={css.cartNotificationBadge} count={currentUserCartItemsCount} />
    ) : null;

  const requestNotificationCountBadge =
    notificationCount.requestNotificationCount > 0 ? (
      <NotificationBadge
        className={css.notificationBadge}
        count={notificationCount.requestNotificationCount}
      />
    ) : null;

  const displayName = user.attributes.profile.firstName;
  const currentPageClass = page => {
    const isAccountSettingsPage =
      page === 'AccountSettingsPage' && ACCOUNT_SETTINGS_PAGES.includes(currentPage);
    const isInboxPage = currentPage?.indexOf('InboxPage') === 0 && page?.indexOf('InboxPage') === 0;
    return currentPage === page || isAccountSettingsPage || isInboxPage ? css.currentPage : null;
  };

  return (
    <div className={css.root}>
      <AvatarLarge className={css.avatar} user={currentUser} />
      <div className={css.content}>
        <span className={css.greeting}>
          <FormattedMessage id="TopbarMobileMenu.greeting" values={{ displayName }} />
        </span>
        <InlineTextButton rootClassName={css.logoutButton} onClick={onLogout}>
          <FormattedMessage id="TopbarMobileMenu.logoutLink" />
        </InlineTextButton>

        <div className={css.accountLinksWrapper}>
          <NamedLink
            className={classNames(css.inbox, currentPageClass('InboxPage'))}
            name="InboxPage"
          >
            <FormattedMessage id="TopbarMobileMenu.inboxLink" />
            {notificationCountBadge}
          </NamedLink>
          {!isCurrentUserAdmin && (
            <NamedLink className={css.inbox} name="CartPage">
              <FormattedMessage id="TopbarMobileMenu.cartLink" />
              {cartItemsCountBadge}
            </NamedLink>
          )}
          <NamedLink
            className={css.navigationLink}
            name="RequestsPage"
            params={{
              tab: isCurrentUserAdmin ? 'received' : 'sent',
            }}
          >
            <FormattedMessage id="TopbarMobileMenu.requestsLink" />
            {requestNotificationCountBadge}
          </NamedLink>
          {isCurrentUserAdmin ? null : (
            <NamedLink className={css.navigationLink} name="SearchPage">
              <FormattedMessage id="TopbarMobileMenu.searchLink" />
            </NamedLink>
          )}
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('ProfileSettingsPage'))}
            name="ProfileSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.profileSettingsLink" />
          </NamedLink>
          <NamedLink
            className={classNames(css.navigationLink, currentPageClass('AccountSettingsPage'))}
            name="AccountSettingsPage"
          >
            <FormattedMessage id="TopbarMobileMenu.accountSettingsLink" />
          </NamedLink>
          {isCurrentUserAdmin ? (
            <NamedLink
              className={classNames(css.navigationLink, currentPageClass('ManageListingsPage'))}
              name="ManageListingsPage"
            >
              <FormattedMessage id="TopbarMobileMenu.yourListingsLink" />
            </NamedLink>
          ) : null}
        </div>
        <CategoryLinks categories={listingCategoriesConfig} />
      </div>
      {isCurrentUserAdmin ? (
        <div className={css.footer}>
          <NamedLink className={css.createNewListingLink} name="NewListingPage">
            <FormattedMessage id="TopbarMobileMenu.newListingLink" />
          </NamedLink>
        </div>
      ) : null}
    </div>
  );
};

TopbarMobileMenu.defaultProps = {
  currentUser: null,
  notificationCount: {
    messageNotificationCount: 0,
    requestNotificationCount: 0,
  },
  currentPage: null,
};

const { bool, func, number, string, shape } = PropTypes;

TopbarMobileMenu.propTypes = {
  isAuthenticated: bool.isRequired,
  currentUserHasListings: bool.isRequired,
  currentUser: propTypes.currentUser,
  currentPage: string,
  notificationCount: shape({
    messageNotificationCount: number,
    requestNotificationCount: number,
  }),
  currentUserCartItemsCount: number,
  onLogout: func.isRequired,
};

export default TopbarMobileMenu;
